import { useEffect, type ComponentProps } from 'react';
import { usePostHog } from 'posthog-js/react';
import { Trans } from '@/UI/Trans/Trans';
import './ErrorPage.css';
import { ErrorSidebar } from '@/components/Common/Error/ErrorSidebar';
import { ErrorDisplay } from '@/components/Common/Error/ErrorDisplay';
import { SmileyXEyesIcon } from '@/UI/Icons/SmileyXEyesIcon';
import { I18N_KEYS } from '@/language/keys';
import { openIntercom } from '@/providers/intercom';
import { posthog as posthogSingleton } from '@/providers/posthog';
import { FF_FPL_MIGRATION } from '@/consts/featureFlags';

/**
 * Generic full-page failure. `actions` is empty by default — the description already tells the
 * viewer to refresh — but callers that can re-run the failed request (e.g. `Recipe`'s workflow
 * fetch) pass a retry action so recovering doesn't require a reload.
 */
export const ErrorPage = ({ actions = [] }: { actions?: ComponentProps<typeof ErrorDisplay>['actions'] }) => {
  const posthog = usePostHog();
  // Read the flag off the posthog singleton instead — it's a module-level instance, unaffected by the React tree.
  const isFigmaThemeEnabled = posthogSingleton.isFeatureEnabled(FF_FPL_MIGRATION) === true;

  useEffect(() => {
    posthog?.capture('error_page_viewed');
  }, [posthog]);

  return (
    <div className="error-page" data-fpl-theme={isFigmaThemeEnabled ? 'true' : undefined}>
      <ErrorSidebar />
      <ErrorDisplay
        icon={<SmileyXEyesIcon />}
        title={<Trans i18nKey={I18N_KEYS.ERROR_PAGE.TITLE}>Oops! Something went wrong</Trans>}
        description={
          <Trans
            i18nKey={I18N_KEYS.ERROR_PAGE.DESCRIPTION}
            components={{
              supportEmail: <button onClick={openIntercom} className="error-page-link-button" />,
            }}
          >
            Try to refresh or contact us at
            <button className="error-page-link-button" onClick={openIntercom}>
              support@weavy.ai
            </button>
          </Trans>
        }
        actions={actions}
      />
    </div>
  );
};
